import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, CheckCircle2, ExternalLink, FileText, Home, Loader2, Star } from 'lucide-react';
import { answerLicenceHosting, submitLicenceSurvey } from '../api/candidate';
import {
  emptySurvey,
  safeReviewUrl,
  surveyPayload,
  surveyReady,
  type LicenceFollowup,
  type SurveyDraft,
} from '../lib/permis';

// Mon permis (refonte du 14/10/2026) : mêmes blocs et mêmes réponses, habillage de la charte.

const QUERY_KEY = ['candidate', 'licence-followup'];

const errorText = (e: unknown, fallback: string) =>
  e && typeof e === 'object' && 'message' in e && (e as { message?: unknown }).message
    ? String((e as { message?: unknown }).message)
    : fallback;

function Choice({ pressed, onClick, disabled, children, label }: { pressed: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode; label?: string }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`min-h-[40px] min-w-[44px] rounded-pill border px-4 py-2 text-sm font-medium transition disabled:opacity-60 ${
        pressed ? 'border-primary bg-primary text-white shadow-btn' : 'border-line bg-white text-body hover:border-primary-200'
      }`}
    >
      {children}
    </button>
  );
}

/** Onglet « Mon permis » : félicitations, pièces pour la fabrication du permis, questionnaire et avis. */
export default function PermisView({ followup, onOpenDocuments }: { followup: LicenceFollowup; onOpenDocuments: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SurveyDraft>(emptySurvey);
  const definition = followup.survey.definition;
  const school = followup.schoolName || 'Votre auto-école';
  const reviews = { school: safeReviewUrl(followup.reviews.school), permio: safeReviewUrl(followup.reviews.permio) };

  const hosting = useMutation({
    mutationFn: answerLicenceHosting,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'document-requests'] });
    },
  });
  const survey = useMutation({
    mutationFn: () => submitLicenceSurvey(surveyPayload(definition, draft)),
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  });
  const ready = surveyReady(definition, draft);

  return (
    <div className="grid gap-5 sm:gap-6">
      <section className="relative overflow-hidden rounded-card bg-panel-indigo p-6 text-white shadow-card sm:p-7">
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/15">
            <Award size={26} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-title-md text-white sm:text-title-lg">Félicitations{followup.firstName ? ` ${followup.firstName}` : ''} !</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-white/85">
              {school} vous félicite : vous avez obtenu votre permis de conduire. Encore bravo et bonne route !
            </p>
          </div>
        </div>
      </section>

      <section aria-labelledby="permis-pieces" className="rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
        <h2 id="permis-pieces" className="flex items-center gap-2 text-title-sm text-heading">
          <FileText size={18} className="text-primary" aria-hidden="true" />
          Pièces pour la fabrication de votre permis
        </h2>
        <p className="mt-1 text-sm text-secondary">Transmettez-les depuis « Mes documents » : votre auto-école s’occupe de la demande de fabrication.</p>
        <ul className="mt-4 grid gap-2" aria-label="Pièces demandées">
          {followup.documents.map((doc) => (
            <li key={doc.docType} className="flex items-center gap-2.5 rounded-row bg-surface-light px-3.5 py-2.5 text-sm text-body">
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              {doc.label}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onOpenDocuments}
          className="mt-4 inline-flex h-10 items-center rounded-pill bg-btn-primary px-5 text-sm font-semibold text-white shadow-btn transition-all hover:-translate-y-0.5 hover:shadow-btn-hover max-sm:w-full max-sm:justify-center"
        >
          Déposer mes pièces
        </button>

        <div className="mt-6 rounded-row border border-line p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-heading">
            <Home size={16} className="text-primary" aria-hidden="true" />
            Êtes-vous hébergé(e) chez un tiers ?
          </h3>
          {followup.hosted === true ? (
            <p className="mt-2 text-sm text-secondary">
              Oui : {followup.hostDocuments.map((d) => d.label.charAt(0).toLowerCase() + d.label.slice(1)).join(', ')} ont été ajoutées à vos documents.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-secondary">
                Si oui, nous vous demanderons aussi : {followup.hostDocuments.map((d) => d.label).join(', ')}.
              </p>
              <div className="mt-2.5 flex gap-2" role="group" aria-label="Êtes-vous hébergé(e) chez un tiers ?">
                <Choice pressed={false} disabled={hosting.isPending} onClick={() => hosting.mutate(true)}>
                  Oui
                </Choice>
                <Choice pressed={followup.hosted === false} disabled={hosting.isPending} onClick={() => hosting.mutate(false)}>
                  Non
                </Choice>
              </div>
            </>
          )}
          {hosting.isError && (
            <p role="alert" className="mt-2 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
              {errorText(hosting.error, 'Réponse non enregistrée. Réessayez.')}
            </p>
          )}
        </div>
      </section>

      <section aria-labelledby="permis-avis" className="rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
        <h2 id="permis-avis" className="flex items-center gap-2 text-title-sm text-heading">
          <Star size={18} className="text-primary" aria-hidden="true" />
          Votre avis sur votre formation
        </h2>
        {followup.survey.answeredAt ? (
          <p role="status" className="mt-3 flex items-center gap-2 rounded-row bg-success-light px-4 py-3 text-sm text-success-dark">
            <CheckCircle2 size={16} aria-hidden="true" />
            Merci pour vos réponses ! Elles aident {school} à progresser.
          </p>
        ) : (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) survey.mutate();
            }}
          >
            {definition.ratings.map((rating) => (
              <div key={rating.key} className="border-t border-slate-100 py-4 first:border-t-0 first:pt-0">
                <p className="mb-2.5 text-sm font-medium text-heading">{rating.label}</p>
                <div className="flex flex-wrap gap-2" role="group" aria-label={rating.label}>
                  {[1, 2, 3, 4, 5].map((note) => (
                    <Choice
                      key={note}
                      label={`${note} sur 5`}
                      pressed={draft.ratings[rating.key] === note}
                      onClick={() => setDraft((d) => ({ ...d, ratings: { ...d.ratings, [rating.key]: note } }))}
                    >
                      {note}
                    </Choice>
                  ))}
                </div>
              </div>
            ))}
            <div className="border-t border-slate-100 py-4">
              <p className="mb-2.5 text-sm font-medium text-heading">{definition.recommend.label}</p>
              <div className="flex gap-2" role="group" aria-label={definition.recommend.label}>
                <Choice pressed={draft.recommend === true} onClick={() => setDraft((d) => ({ ...d, recommend: true }))}>
                  Oui
                </Choice>
                <Choice pressed={draft.recommend === false} onClick={() => setDraft((d) => ({ ...d, recommend: false }))}>
                  Non
                </Choice>
              </div>
            </div>
            <label className="block border-t border-slate-100 py-4">
              <span className="text-sm font-medium text-heading">{definition.comment.label}</span>
              <textarea
                value={draft.comment}
                maxLength={definition.comment.maxLength}
                rows={3}
                onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
                className="mt-2 w-full rounded-xl border border-line bg-surface-light px-3.5 py-2.5 text-sm text-body outline-none focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
              />
            </label>
            {survey.isError && (
              <p role="alert" className="mb-3 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
                {errorText(survey.error, 'Réponses non envoyées. Réessayez.')}
              </p>
            )}
            <button
              type="submit"
              disabled={!ready || survey.isPending}
              className="flex h-10 items-center gap-2 rounded-pill bg-btn-primary px-5 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:cursor-not-allowed disabled:opacity-45 max-sm:w-full max-sm:justify-center"
            >
              {survey.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              Envoyer mes réponses
            </button>
          </form>
        )}
      </section>

      {(reviews.school || reviews.permio) && (
        <section aria-labelledby="permis-reviews" className="rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
          <h2 id="permis-reviews" className="text-title-sm text-heading">Partagez votre expérience</h2>
          <p className="mt-1 text-sm text-secondary">Votre avis public aide les futurs élèves à choisir. Quelques secondes suffisent.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {reviews.school && (
              <a
                href={reviews.school}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-pill border border-line bg-white px-[18px] text-sm font-medium text-body transition-all hover:-translate-y-px hover:border-primary-200 hover:shadow-navbar [&_svg]:text-primary"
              >
                Laisser un avis Google à {school}
                <ExternalLink size={14} aria-hidden="true" />
              </a>
            )}
            {reviews.permio && (
              <a
                href={reviews.permio}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-pill border border-line bg-white px-[18px] text-sm font-medium text-body transition-all hover:-translate-y-px hover:border-primary-200 hover:shadow-navbar [&_svg]:text-primary"
              >
                Donner mon avis sur Permio
                <ExternalLink size={14} aria-hidden="true" />
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
